import { legacyLinksSection } from "./legacyLinks";
import {downloadText} from '../shared/download';
import "../queueFleet.css";
import "./links.css";
import { api, ApiError } from "../../api";
import { emptyBlock, errorBanner, loadingBlock, pill, tableRegion } from "../shared/blocks";
import { createLifecycle, isAbort } from "../shared/lifecycle";
import { keepFocus } from "../shared/focus";
import { clientHref, h, safeMessage, safeUrl, tidy, type Child } from "../shared/safe";
import { clientWorkspaceNav } from "../client/workspaceNav";
import { platformLabel } from "../../platforms";
import {
  COUNTED_STATES,
  LINK_STATES,
  STATE_FILTERS,
  STATE_META,
  UNCONFIRMED_TEXT,
  anchorTypeLabel,
  currentAttemptLabel,
  describeRecheck,
  parseDeployments,
  parseRecheck,
  sourcePages,
  SYNTHETIC_SUFFIX,
  type SourcePageView,
  filterLinks,
  followLabel,
  formLabel,
  numberProblems,
  parseClientLinks,
  parseStateFilter,
  parseSummary,
  publicationStateLabel,
  relText,
  sourceKindLabel,
  sumActive,
  targetKindLabel,
  unconfirmedPublications,
  type ClientLinksView,
  type Counts,
  type HistoryReceiptView,
  type LinkState,
  type LinkView,
  type PublicationView,
  type StateFilter,
  type SummaryClientView,
  type TargetView,
  type UrlView,
} from "./linksModel";

// Created links: the links CloudPush itself created, with the exact source and target of each, what each state does and does not
// prove, and counts that include only delivered (not inspected) and observed links. Built with h(): every server string is text,
// URLs become links only when they are http(s). It lists links CloudPush created from its own receipts; it does not look for
// backlinks anywhere.

const enc = encodeURIComponent;

/** http(s) addresses open in a new tab without giving the opened page access to this one; anything else is plain text. */
function urlLink(u: UrlView | null, empty: Child = "—"): Child {
  if (u === null) return empty;
  if (u.href === null) return h("span", { class: "qf-wrap", title: "Not a web address, so it is shown as text" }, u.text);
  return h("a", { class: "qf-wrap", href: safeUrl(u.href), target: "_blank", rel: "noopener noreferrer nofollow" }, u.text);
}

const dash = "—";

export const clientLinksHref = (id: unknown) => `${clientHref(id)}/links`;

function stateBadge(state: LinkState | null, raw: string): HTMLElement {
  if (state === null) return h("span", { class: "pill neutral", dataset: { stateBadge: "unknown" } }, `Unknown state: ${raw || "none"}`);
  const m = STATE_META[state];
  return h("span", { class: `pill ${m.tone}`, dataset: { stateBadge: state } }, m.label);
}

function notice(tone: "warn" | "bad" | "info", role: "alert" | "status", ...children: Child[]): HTMLElement {
  return h("div", { class: `ql-notice ql-notice--${tone}`, role }, ...children);
}

// ---- legend -------------------------------------------------------------------------------------------------------------

function legend(ruleStates: Partial<Record<LinkState, string>> = {}): HTMLElement {
  return h(
    "details",
    { class: "card ql-legend", dataset: { legend: "true" } },
    h("summary", null, "How link statuses and counts work"),
    h(
      "p",
      { class: "muted" },
      "This report lists the links CloudPush created: links it rendered into sites or submitted in posts, from its own delivery receipts. It is not a list of backlinks found anywhere and nothing here crawls the web. ",
      "The counted number is delivered (not inspected) plus observed. Planned, uncertain and absent links are listed but never counted.",
    ),
    h(
      "dl",
      { class: "ql-legend__list" },
      ...LINK_STATES.flatMap((s) => {
        const m = STATE_META[s];
        return [
          h("dt", { dataset: { legendState: s } }, stateBadge(s, s), " ", h("span", { class: "muted" }, m.counted ? "counted" : "not counted")),
          h(
            "dd",
            { dataset: { legendFor: s } },
            h("div", null, h("strong", null, "Proves: "), m.proves),
            h("div", null, h("strong", null, "Does not prove: "), m.doesNotProve),
            ruleStates[s] ? h("div", { class: "muted ql-rule" }, `Server rule: ${ruleStates[s]}`) : null,
          ),
        ];
      }),
    ),
  );
}

// ---- counts ---------------------------------------------------------------------------------------------------------------

const stat = (n: number, label: string, key: string, sub?: string) =>
  h("div", { class: "qf-stat", dataset: { stat: key } }, h("div", { class: "qf-stat__n" }, String(n)), h("div", { class: "qf-stat__l" }, label), sub ? h("div", { class: "qf-stat__l ql-stat__sub" }, sub) : null);

function headline(view: ClientLinksView): HTMLElement {
  const t = view.totals;
  return h(
    "section",
    { class: "ql-section", "aria-labelledby": "ql-counts-h", dataset: { section: "counts" } },
    h("h2", { id: "ql-counts-h", class: "qf-sr-only" }, "Counts"),
    h(
      "div",
      { class: "qf-stats ql-stats" },
      stat(t.active, "Created links", "active", `${t.deliveredUninspected} delivered (not inspected) + ${t.observed} observed`),
      stat(t.sourcePages, "Pages with links", "sourcePages", "Pages containing counted links"),
      stat(t.sourceHosts, "Source websites", "sourceHosts", "Unique hosts with counted links"),
      stat(t.targets, "Target pages", "targets", "Destinations linked to"),
    ),
    h(
      "div",
      { class: "ql-notcounted", role: "group", "aria-label": "Listed but not counted" },
      h("strong", null, "Not included in total: "),
      h("span", { class: "ql-chip", dataset: { stat: "planned" } }, `${t.planned} planned`),
      h("span", { class: "ql-chip", dataset: { stat: "uncertain" } }, `${t.uncertain} uncertain`),
      h("span", { class: "ql-chip", dataset: { stat: "absent" } }, `${t.absent} absent`),

    ),
    t.byTargetKind.length || t.bySourceKind.length
      ? h(
          "details",
          { class: "ql-facets" },
          h("summary", null, "Breakdown by target and page type"),
          facet("By kind of target", "byTargetKind", t.byTargetKind, targetKindLabel),
          facet("By kind of source page", "bySourceKind", t.bySourceKind, sourceKindLabel),
          h("p", { class: "muted ql-facet-note" }, "Each list counts the same counted links from a different angle. Do not add the lists together."),
        )
      : null,
  );
}

function facet(title: string, key: string, rows: { key: string; count: number }[], label: (k: string) => string): HTMLElement {
  return h(
    "div",
    { class: "ql-facet", dataset: { facet: key } },
    h("h3", null, title),
    rows.length ? h("ul", null, ...rows.map((r) => h("li", { dataset: { facetKey: r.key } }, `${label(r.key)}: `, h("strong", null, String(r.count))))) : h("p", { class: "muted" }, "None"),
  );
}

// ---- targets --------------------------------------------------------------------------------------------------------------

function targetsTable(targets: TargetView[]): HTMLElement {
  if (targets.length === 0) return h("p", { class: "muted" }, "No target page has a counted or listed link yet.");
  const th = (label: string, extra?: string) => h("th", { scope: "col", class: extra }, label);
  const table = h(
    "table",
    { class: "qf-table ql-table" },
    h("caption", { class: "qf-sr-only" }, "Created links per target page. Counted columns first, then listed-but-not-counted columns."),
    h(
      "thead",
      null,
      h("tr", null, th("Target page"), th("Counted"), th("Delivered (not inspected)"), th("Observed"), th("Source pages"), th("Source hosts"), th("Planned (not counted)", "ql-nc"), th("Uncertain (not counted)", "ql-nc"), th("Absent (not counted)", "ql-nc")),
    ),
    h(
      "tbody",
      null,
      ...targets.map((t) =>
        h(
          "tr",
          { dataset: { targetUrl: t.targetUrl?.text ?? "", targetKind: t.kind ?? "" } },
          h("td", null, urlLink(t.targetUrl), t.label ? h("div", { class: "qf-strong qf-wrap" }, t.label) : null, h("div", { class: "muted qf-wrap" }, targetKindLabel(t.kind))),
          h("td", { dataset: { col: "active" } }, h("strong", null, String(t.counts.active))),
          h("td", { dataset: { col: "deliveredUninspected" } }, String(t.counts.deliveredUninspected)),
          h("td", { dataset: { col: "observed" } }, String(t.counts.observed)),
          h("td", { dataset: { col: "sourcePages" } }, String(t.counts.sourcePages)),
          h("td", { dataset: { col: "sourceHosts" } }, String(t.counts.sourceHosts)),
          h("td", { class: "ql-nc", dataset: { col: "planned" } }, String(t.counts.planned)),
          h("td", { class: "ql-nc", dataset: { col: "uncertain" } }, String(t.counts.uncertain)),
          h("td", { class: "ql-nc", dataset: { col: "absent" } }, String(t.counts.absent)),
        ),
      ),
    ),
  );
  return tableRegion("Created links per target page", table);
}

// ---- links ----------------------------------------------------------------------------------------------------------------

const INSPECTION: Record<string, string> = {
  link_found: "link found",
  link_not_found: "link not found",
  inaccessible: "page could not be reached",
  inconclusive: "this link could not be checked",
  ambiguous: "could not tell which link it was",
};

function linkRow(l: LinkView): HTMLElement {
  const countedByPage = l.state !== null && COUNTED_STATES.includes(l.state) && l.counted;
  const src = l.source;
  const sourceCell = src.url
    ? [urlLink(src.url), src.path && src.url.href !== null && src.url.text.endsWith(src.path) ? null : src.path ? h("div", { class: "muted qf-wrap" }, `path ${src.path}`) : null]
    : [h("div", { class: "qf-strong" }, "Not yet published"), h("div", { class: "muted qf-wrap" }, `Planned page: ${src.path ?? "unknown"}`), h("div", { class: "muted" }, "No source URL exists until it is delivered.")];
  const t = l.target;
  const anchorText = l.anchor.text !== null && l.anchor.text !== "" ? `“${l.anchor.text}”` : l.anchor.hasImage ? `image${l.anchor.imageAlt ? ` (alt “${l.anchor.imageAlt}”)` : ""}` : "(no anchor text)";
  const insp = l.inspection;
  return h(
    "tr",
    { dataset: { linkId: l.id, state: l.state ?? "unknown", counted: countedByPage ? "true" : "false", sourceKind: src.kind ?? "", targetKind: t.kind ?? "" } },
    h("td", null, stateBadge(l.state, l.stateRaw), h("div", { class: "muted" }, countedByPage ? "Counted" : "Not counted")),
    h("td", { class: "qf-wrap", dataset: { cell: "source" } }, ...sourceCell),
    h(
      "td",
      { class: "qf-wrap", dataset: { cell: "target" } },
      urlLink(t.href),
      t.comparisonUrl && t.href && t.comparisonUrl.text !== t.href.text ? h("div", { class: "muted qf-wrap" }, `Matched as ${t.comparisonUrl.text}`) : null,
      t.label ? h("div", { class: "qf-strong qf-wrap" }, t.label) : null,
      h("div", { class: "muted" }, targetKindLabel(t.kind)),
    ),
    h(
      "td",
      { class: "qf-wrap", dataset: { cell: "anchor" } },
      h("div", null, anchorText),
      h("div", { class: "muted" }, `${anchorTypeLabel(l.anchor.type)} anchor · ${formLabel(l.form)}`),
      h("div", { class: "muted" }, `${relText(l)} · ${followLabel(l.rel.followType)}`),
      l.rel.observedTokens !== null ? h("div", { class: "muted" }, `rel seen when checked${l.rel.observedAt ? ` (${l.rel.observedAt})` : ""}: ${l.rel.observedTokens.length ? l.rel.observedTokens.join(" ") : "none"}`) : null,
      l.repeatsOnPage > 1 ? h("div", { class: "muted" }, `Repeated ${l.repeatsOnPage} times on this page; counted once.`) : null,
    ),
    h(
      "td",
      { class: "qf-wrap", dataset: { priority: "mid", cell: "type" } },
      h("div", null, `${sourceKindLabel(src.kind)} → ${targetKindLabel(t.kind)}`),
      h("div", { class: "muted" }, src.platform ? `${platformLabel(src.platform)}${src.platformCategory ? ` (${src.platformCategory.replace(/_/g, " ")})` : ""}` : dash),
      insp
        ? h("div", { class: "muted", dataset: { inspection: insp.result ?? "" } }, `Checked${insp.checkedAt ? ` ${insp.checkedAt}` : ""}: ${INSPECTION[insp.result ?? ""] ?? insp.result ?? "unknown result"}${insp.httpStatus !== null ? ` (HTTP ${insp.httpStatus})` : ""}${insp.note ? `. ${insp.note}` : ""}`)
        : l.state === "delivered_uninspected"
          ? h("div", { class: "muted" }, "Not checked on the live page.")
          : null,
    ),
  );
}

function linksTable(links: LinkView[]): HTMLElement {
  const th = (label: string, priority?: string) => h("th", { scope: "col", dataset: { priority } }, label);
  const table = h(
    "table",
    { class: "qf-table ql-table ql-links" },
    h("caption", { class: "qf-sr-only" }, `Created links: ${links.length} shown.`),
    h("thead", null, h("tr", null, th("State"), th("Source (where it is)"), th("Target (where it points)"), th("Anchor and rel"), th("Type and checks", "mid"))),
    h("tbody", null, ...links.map(linkRow)),
  );
  return tableRegion("Created links", table);
}

// ---- publications and history ------------------------------------------------------------------------------------------------

function publicationsTable(pubs: PublicationView[]): HTMLElement {
  if (pubs.length === 0) return h("p", { class: "muted" }, "No publication has been submitted for this client.");
  const th = (label: string) => h("th", { scope: "col" }, label);
  const chips = (c: Counts) => `${c.active} counted · ${c.planned} planned · ${c.uncertain} uncertain · ${c.absent} absent`;
  const table = h(
    "table",
    { class: "qf-table ql-table" },
    h("caption", { class: "qf-sr-only" }, "Publications and what is known about their links."),
    h("thead", null, h("tr", null, th("Article"), th("Destination"), th("State"), th("Link evidence"), th("Links"), th("Receipts"), th("Note"))),
    h(
      "tbody",
      null,
      ...pubs.map((p) => {
        const st = publicationStateLabel(p.state);
        return h(
          "tr",
          { dataset: { publicationState: p.state ?? "unknown", evidence: p.evidence } },
          h("td", { class: "qf-wrap" }, p.title),
          h("td", null, p.platform ? platformLabel(p.platform) : dash, p.platformCategory ? h("div", { class: "muted" }, p.platformCategory.replace(/_/g, " ")) : null),
          h("td", null, pill(st.label, st.tone)),
          h(
            "td",
            null,
            p.evidence === "unconfirmed" ? pill("Unconfirmed", "warn") : p.evidence === "established" ? pill("Established", "ok") : p.evidence === "none" ? h("span", { class: "muted" }, "None yet") : h("span", { class: "muted" }, "Unknown"),
          ),
          h("td", { class: "qf-wrap" }, chips(p.links)),
          h("td", null, `${p.currentReceipts} current`, h("div", { class: "muted" }, `${p.previousAccountReceipts} from a previous account (history)`)),
          h(
            "td",
            { class: "qf-wrap" },
            p.note ?? dash,
            p.pinnedToCurrentAccount === false ? h("div", { class: "muted" }, "Its latest job is pinned to a previous account.") : null,
            currentAttemptLabel(p.currentAttempt) ? h("div", { class: "muted", dataset: { currentAttempt: p.currentAttempt ?? "" } }, `Current account: ${currentAttemptLabel(p.currentAttempt)}`) : null,
            p.previousAccountDelivered === true && p.state !== "previous_account_only" ? h("div", { class: "muted", dataset: { previousDelivered: "true" } }, "A previous account also delivered this (history, not counted).") : null,
          ),
        );
      }),
    ),
  );
  return tableRegion("Publications", table);
}

function historyReceipt(r: HistoryReceiptView): HTMLElement {
  const th = (label: string) => h("th", { scope: "col" }, label);
  return h(
    "article",
    { class: "card ql-history__receipt", dataset: { historyReceipt: r.receiptId } },
    h("p", null, pill(r.kind === "superseded_release" ? "History of an earlier release" : "History of a previous account", "neutral"), " ", h("strong", null, "Not counted.")),
    h(
      "dl",
      { class: "deflist" },
      h("dt", null, "Destination"),
      h("dd", null, r.platform ? platformLabel(r.platform) : dash),
      h("dt", null, "Receipt URL"),
      h("dd", null, urlLink(r.providerUrl)),
      h("dt", null, "Delivered"),
      h("dd", null, r.deliveredAt ?? dash),
      h("dt", null, "Account"),
      h("dd", { class: "qf-wrap" }, r.accountId ? `${r.accountId}${r.accountRevision !== null ? `, credentials revision ${r.accountRevision}` : ""}` : "not recorded"),
      h("dt", null, "Why it is history"),
      h("dd", { class: "qf-wrap" }, r.reason ?? "It belongs to a previous account or credential revision."),
    ),
    r.links.length
      ? tableRegion(
          "Links in this earlier delivery",
          h(
            "table",
            { class: "qf-table ql-table" },
            h("caption", { class: "qf-sr-only" }, "Links recorded in an earlier delivery. This historical receipt is not counted separately."),
            h("thead", null, h("tr", null, th("Source"), th("Target"), th("Anchor"), th("rel"))),
            h(
              "tbody",
              null,
              ...r.links.map((l) =>
                h(
                  "tr",
                  { dataset: { historyLink: "true" } },
                  h("td", { class: "qf-wrap" }, urlLink(l.sourceUrl)),
                  h("td", { class: "qf-wrap" }, urlLink(l.href)),
                  h("td", { class: "qf-wrap" }, l.anchorText ?? dash),
                  h("td", { class: "qf-wrap" }, l.rel === null ? "unknown" : l.rel.length ? l.rel.join(" ") : "none"),
                ),
              ),
            ),
          ),
        )
      : h("p", { class: "muted" }, "The links of this delivery could not be listed."),
  );
}

function historySection(view: ClientLinksView): HTMLElement {
  return h(
    "section",
    { class: "ql-section ql-history", "aria-labelledby": "ql-history-h", dataset: { section: "history" } },
    h("h2", { id: "ql-history-h" }, view.history.receipts.some((r) => r.kind === "superseded_release") ? "History of earlier releases and accounts (not counted)" : "History of a previous account (not counted)"),
    h("p", { class: "muted" }, view.history.note ?? "Receipts of a previous account or credential revision are evidence of what was delivered then. They are never counted for the current account."),
    view.history.receipts.length ? h("div", null, ...view.history.receipts.map(historyReceipt)) : h("p", { class: "muted", dataset: { historyEmpty: "true" } }, "No receipts from a previous account."),
  );
}

// ---- the per-client page ----------------------------------------------------------------------------------------------

// ---- source pages and Re-check ----------------------------------------------------------------------------------------------------

interface RecheckState {
  busy: boolean;
  result: { tone: "ok" | "warn" | "bad"; text: string } | null;
}

function sourcePagesSection(pages: SourcePageView[], deployments: Map<string, string> | null, deploymentsError: boolean, rechecks: Map<string, RecheckState>, run: (page: SourcePageView, deploymentId: string) => void): HTMLElement {
  const stateCounts = (p: SourcePageView) =>
    LINK_STATES.filter((st) => p.byState[st]).map((st) => `${p.byState[st]} ${STATE_META[st].label.toLowerCase()}`).join(", ");
  const rows = pages.map((p) => {
    const st = rechecks.get(p.url);
    const id = deployments?.get(p.url) ?? null;
    const action: Child[] = [];
    if (p.synthetic) action.push(h("div", { class: "muted", dataset: { recheck: "synthetic" } }, `Synthetic receipts are never verified: this page's address ends in ${SYNTHETIC_SUFFIX}, which can never be fetched. Nothing is called.`));
    else if (deploymentsError) action.push(h("div", { class: "muted", dataset: { recheck: "unavailable" } }, "The list of checkable pages could not be loaded, so this page cannot be re-checked now."));
    else if (deployments === null) action.push(h("div", { class: "muted", dataset: { recheck: "loading" } }, "Loading…"));
    else if (id === null) action.push(h("div", { class: "muted", dataset: { recheck: "no-record" } }, "No deployment record matches this page, so it cannot be re-checked from here."));
    else
      action.push(
        h(
          "button",
          {
            type: "button",
            class: "secondary",
            dataset: { recheck: "button", focus: `recheck:${p.url}` },
            "aria-busy": st?.busy ? "true" : false,
            "aria-disabled": st?.busy ? "true" : false,
            "aria-label": `Re-check ${tidy(p.url, 120)}`,
            on: { click: () => (st?.busy ? undefined : run(p, id)) },
          },
          st?.busy ? "Checking…" : "Re-check this page",
        ),
      );
    if (st?.result) action.push(h("div", { class: `ql-recheck ql-recheck--${st.result.tone}`, role: "status", dataset: { recheckResult: st.result.tone } }, st.result.text));
    return h(
      "tr",
      { dataset: { sourcePage: p.url, synthetic: p.synthetic ? "true" : "false" } },
      h("td", { class: "qf-wrap" }, urlLink(p.urlView), h("div", { class: "muted" }, sourceKindLabel(p.kind))),
      h("td", { class: "qf-wrap" }, stateCounts(p) || "—"),
      h("td", { class: "qf-wrap muted" }, p.lastCheck ? `${p.lastCheck.checkedAt ?? "recorded"}: ${INSPECTION[p.lastCheck.result ?? ""] ?? p.lastCheck.result ?? "unknown result"}${p.lastCheck.httpStatus !== null ? ` (HTTP ${p.lastCheck.httpStatus})` : ""}` : "Not checked on the live page."),
      h("td", { class: "qf-wrap" }, ...action),
    );
  });
  const th = (label: string) => h("th", { scope: "col" }, label);
  return h(
    "section",
    { class: "ql-section", "aria-labelledby": "ql-sources-h", dataset: { section: "sources" } },
    h("h2", { id: "ql-sources-h" }, "Source pages"),
    h("p", { class: "muted" }, "The pages CloudPush published. Re-check fetches a page now and looks for the exact target address of each link; it proves only what it found at that moment (not that the link stays, that the page is indexed, or any ranking effect)."),
    tableRegion("Source pages", h("table", { class: "qf-table ql-table" }, h("caption", { class: "qf-sr-only" }, `${pages.length} source pages.`), h("thead", null, h("tr", null, th("Page"), th("Links by state"), th("Last check"), th("Check now"))), h("tbody", null, ...rows))),
  );
}

export async function clientLinksView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "qf ql" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  const clientLabel = h("a", { href: clientHref(clientId) }, "Client workspace");
  const topbar = h("div", { class: "topbar ql-header" },
    h("div", null, h("div", { class: "ql-breadcrumb" }, h("a", { href: "#/clients" }, "Clients"), h("span", { "aria-hidden": "true" }, "/"), clientLabel),
      h("h1", null, "Created links"), h("p", { class: "ql-description" }, "See where your published pages link, and what has been confirmed.")),
    clientId ? h("a", { class: "btn secondary", href: `${clientHref(clientId)}/websites` }, "View websites") : null);
  const intro = h("p", { class: "qf-summary", dataset: { intro: "true" } }, "Links CloudPush created for this client.");
  const body = h("div", { class: "ql-body" });
  shell.append(topbar, ...(clientId ? [clientWorkspaceNav(clientId, "Links")] : []), intro, body, ...(clientId ? [legacyLinksSection(clientId)] : []), legend());
  if (clientId === "") {
    body.replaceChildren(errorBanner("This page needs a client. Go to the Fleet and open one."));
    return;
  }

  // The filter controls are built once so typing and choosing keep focus; only what is beneath them is rebuilt.
  let state: StateFilter = "all";
  let query = "";
  let view: ClientLinksView | null = null;
  let evidenceFiltered = false;
  const stateSelect = h(
    "select",
    { id: "links-state", "aria-controls": "links-results" },
    ...STATE_FILTERS.map((f) => h("option", { value: f }, f === "all" ? "All states" : STATE_META[f].label)),
  ) as HTMLSelectElement;
  const queryInput = h("input", { id: "links-q", type: "search", placeholder: "Source, target or anchor text", autocomplete: "off", "aria-controls": "links-results" }) as HTMLInputElement;
  const toolbar = h(
    "div",
    { class: "qf-toolbar", role: "search", "aria-label": "Filter created links" },
    h("div", null, h("label", { class: "qf-label", for: "links-state" }, "State"), stateSelect),
    h("div", { class: "qf-grow" }, h("label", { class: "qf-label", for: "links-q" }, "Search the list"), queryInput),
  );
  const extraFilters:Record<string,string>={};
  const advanced=h('details',{class:'ql-advanced'},h('summary',null,'More filters'));
  const advancedFields=h('div',{class:'ql-advanced__fields'});advanced.append(advancedFields);
  const filterInputs=new Map<string,HTMLInputElement|HTMLSelectElement>();
  for(const [key,label] of [['platform','Provider'],['targetUrl','Exact target URL'],['sourceHost','Source host'],['satelliteId','Site context'],['campaignId','Campaign']] as const){
    const input=['platform','satelliteId','campaignId'].includes(key)?h('select',{'aria-label':label},h('option',{value:''},'All'),...(key==='satelliteId'?[h('option',{value:'main'},'Client main site')]:[])):h('input',{'aria-label':label,type:key==='targetUrl'?'url':'text'});
    filterInputs.set(key,input);advancedFields.append(h('label',null,label,' ',input));
  }
  const sourceKind=h('select',{'aria-label':'Source page kind'},h('option',{value:''},'All page kinds'),...['site_home','site_blog','site_service','site_location','site_about','site_contact','site_other','content_post'].map(k=>h('option',{value:k},k.replaceAll('_',' '))));filterInputs.set('sourceKind',sourceKind);advancedFields.append(h('label',null,'Source page kind',sourceKind));
  for(const [key,label] of [['from','Delivered from'],['to','Delivered before']] as const){const input=h('input',{'aria-label':label,type:'datetime-local'});filterInputs.set(key,input);advancedFields.append(h('label',null,label,' ',input));}
  const choices=h('button',{type:'button',class:'secondary'},'Load campaign and satellite choices');choices.onclick=async()=>{choices.disabled=true;try{
    const [campaigns,satellites]=await Promise.all([api<{campaigns:{id:string;name:string}[]}>(`/v1/campaigns?clientId=${enc(clientId)}`),api<{satellites:{id:string;service_key:string}[]}>(`/v1/sites/satellites?clientId=${enc(clientId)}`)]);
    const cp=filterInputs.get('campaignId') as HTMLSelectElement,sp=filterInputs.get('satelliteId') as HTMLSelectElement;cp.replaceChildren(h('option',{value:''},'All campaigns'),...campaigns.campaigns.map(c=>h('option',{value:c.id},c.name)));sp.replaceChildren(h('option',{value:''},'All sites'),h('option',{value:'main'},'Client main site'),...satellites.satellites.map(s=>h('option',{value:s.id},s.service_key)));
  }catch(e){listNote.textContent=safeMessage(e instanceof Error?e.message:e);}finally{choices.disabled=false;}};
  const applyFilters=h('button',{type:'button'},'Apply evidence filters');applyFilters.onclick=()=>{for(const [k,v] of filterInputs){if(v.value)extraFilters[k]=['from','to'].includes(k)?String(Math.floor(new Date(v.value).getTime()/1000)):v.value;else delete extraFilters[k];}void load();};
  const exportCsv=h('button',{type:'button',class:'secondary','aria-label':'Download filtered links CSV'},'Export CSV');exportCsv.onclick=async()=>{exportCsv.disabled=true;try{const qs=new URLSearchParams(extraFilters);if(state!=='all')qs.set('state',state);if(query)qs.set('q',query);const r=await api<{csv:string;rows:number}>(`/v1/clients/${enc(clientId)}/links/export${qs.size?'?'+qs:''}`);downloadText(r.csv,'text/csv;charset=utf-8','cloudpush-created-links.csv');listNote.textContent=`Exported ${r.rows} recorded occurrences with their states and evidence. No publication occurred.`;}catch(e){listNote.textContent=safeMessage(e instanceof Error?e.message:e);}finally{exportCsv.disabled=false;}};
  advancedFields.append(choices,applyFilters,h('p',{class:'ql-filter-note'},'Date filters select recorded delivery activity. Current state is read now; history and whole-client totals remain separate.'));
  toolbar.classList.add("ql-toolbar");
  toolbar.append(exportCsv);
  const listNote = h("p", { class: "qf-summary", role: "status", dataset: { listNote: "true" } });
  const results = h("div", { id: "links-results" });
  const holders = {
    counts: h("div"),
    notices: h("div", { dataset: { notices: "true" } }),
    targets: h("section", { class: "ql-section", "aria-labelledby": "ql-targets-h", dataset: { section: "targets" } }),
    sources: h("div", { dataset: { sourcesHost: "true" } }),
    links: h("section", { class: "ql-section", "aria-labelledby": "ql-links-h", dataset: { section: "links" } }, h("div", { class: "ql-panel-heading" }, h("h2", { id: "ql-links-h" }, "Link library"), h("span", { class: "muted" }, "Source → target")), toolbar, advanced, listNote, results),
    publications: h("section", { class: "ql-section", "aria-labelledby": "ql-pubs-h", dataset: { section: "publications" } }),
    history: h("div"),
  };
  let built = false;

  function paintList() {
    if (!view) return;
    const visible = filterLinks(view.links, query);
    const filteredByState = state !== "all" || evidenceFiltered;
    listNote.textContent = `Showing ${visible.length} of ${view.totals.links} ${view.totals.links === 1 ? "link" : "links"}${state !== "all" ? ` in the state “${STATE_META[state].label}”` : ""}${query.trim() ? ` matching “${tidy(query.trim(), 60)}”` : ""}. The counts above always describe every link.`;
    if (visible.length === 0) {
      const confirmed = unconfirmedPublications(view).length > 0;
      results.replaceChildren(
        emptyBlock(
          filteredByState || query.trim() ? "No link matches that filter" : confirmed ? "No links are listed" : view.publications.length ? "No link records yet" : "No links yet",
          filteredByState || query.trim() ? "Choose another state or clear the search. This is not a count of zero." : confirmed ? "See the notice above: CloudPush could not confirm which links the delivery contained." : view.publications.length ? "These publications do not have recorded links yet. Open Websites to see your existing sites." : "Nothing has been delivered or queued that contains a link.",
        ),
      );
      return;
    }
    results.replaceChildren(linksTable(visible));
  }

  function paint() {
    if (!view) return;
    const v = view;
    clientLabel.textContent = v.clientName || v.clientSlug || v.clientId;
    intro.replaceChildren("Only links recorded by CloudPush are included.", ...(v.generatedAt ? [h("span", { class: "ql-updated" }, `Updated ${v.generatedAt}`)] : []));
    // the legend's server rules replace the fixed text only as an addition: re-render it in place
    const old = shell.querySelector(".ql-legend");
    if (old) { const next = legend(v.rules.states); if ((old as HTMLDetailsElement).open) next.setAttribute("open", ""); old.replaceWith(next); }
    const empty = v.links.length === 0 && v.publications.length === 0 && v.history.receipts.length === 0 && v.warnings.length === 0 && v.totals.links === 0;
    const problems = numberProblems(v, state !== "all" || evidenceFiltered);
    const unconfirmed = unconfirmedPublications(v);
    const notices: HTMLElement[] = [];
    if (unconfirmed.length || v.warnings.length) {
      notices.push(
        notice(
          "warn",
          "alert",
          h("strong", null, "Unconfirmed: "),
          UNCONFIRMED_TEXT,
          unconfirmed.length ? h("ul", { class: "ql-list" }, ...unconfirmed.map((p) => h("li", null, `${p.title} (${p.platform ? platformLabel(p.platform) : "destination"}): delivered, link evidence unconfirmed`))) : null,
          v.warnings.length ? h("ul", { class: "ql-list", dataset: { warnings: "true" } }, ...v.warnings.map((w) => h("li", null, w))) : null,
        ),
      );
    }
    if (problems.length) notices.push(notice("bad", "alert", h("strong", null, "The numbers disagree. "), "Do not rely on the figures until this is understood.", h("ul", { class: "ql-list", dataset: { problems: "true" } }, ...problems.map((p) => h("li", null, p)))));
    if (v.publications.some((p) => p.state === "queued") && v.totals.planned > 0) notices.push(notice("info", "status", "Some links are planned: their publication is accepted but not delivered. They have no source URL yet and are not counted."));
    holders.notices.replaceChildren(...notices);
    if (empty) {
      holders.counts.replaceChildren(
        emptyBlock("Nothing has been published for this client yet", "No publication has been submitted, so there are no created links. Publish an approved article from the client page.", [h("a", { class: "btn", href: clientHref(v.clientId) }, "Open the client")]),
      );
      for (const k of ["targets", "publications", "sources"] as const) holders[k].replaceChildren();
      holders.history.replaceChildren();
      holders.links.hidden = true;
      return;
    }
    holders.links.hidden = false;
    holders.counts.replaceChildren(headline(v));
    holders.targets.replaceChildren(h("h2", { id: "ql-targets-h" }, "Per target page"), h("p", { class: "muted" }, "Counted links, source pages and source hosts per target. Planned, uncertain and absent links have their own columns and are never part of the counted figures."), targetsTable(v.targets));
    holders.publications.replaceChildren(h("h2", { id: "ql-pubs-h" }, "Publications"), publicationsTable(v.publications));
    paintSources();
    holders.history.replaceChildren(historySection(v));
    paintList();
  }

  // ---- source pages and Re-check -------------------------------------------------------------------------------------------------
  let deployments: Map<string, string> | null = null;
  let deploymentsError = false;
  let deploymentsRequested = false;
  const rechecks = new Map<string, RecheckState>();

  function paintSources() {
    if (!view) return;
    if (state !== "all") {
      holders.sources.replaceChildren(h("p", { class: "muted", dataset: { sourcesNote: "true" } }, "Source pages are listed when the state filter is All states (a filtered list would show only some pages)."));
      return;
    }
    const pages = sourcePages(view.links);
    if (pages.length === 0) return void holders.sources.replaceChildren();
    keepFocus(shell, () => holders.sources.replaceChildren(sourcePagesSection(pages, deployments, deploymentsError, rechecks, (p, id) => void runRecheck(p, id))));
  }

  async function loadDeployments() {
    if (deploymentsRequested) return;
    deploymentsRequested = true;
    try {
      const map = parseDeployments(await api<unknown>(`/v1/verification?clientId=${enc(clientId)}`));
      if (!life.alive()) return;
      deployments = map;
      deploymentsError = map === null;
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      deploymentsError = true;
    }
    paintSources();
  }

  async function runRecheck(p: SourcePageView, id: string) {
    if (p.synthetic) return; // never called for a synthetic receipt
    const key = p.url;
    if (rechecks.get(key)?.busy) return;
    rechecks.set(key, { busy: true, result: null });
    paintSources();
    let result: RecheckState["result"];
    try {
      const reply = parseRecheck(await api<unknown>(`/v1/verification/run/${enc(id)}`, { method: "POST" }));
      if (!life.alive()) return;
      result = reply ? describeRecheck(reply) : { tone: "warn", text: "The server replied, but not with a check result this page recognises, so nothing is confirmed. The report below was reloaded from what the server recorded." };
    } catch (e) {
      if (!life.alive() || isAbort(e)) return;
      result =
        e instanceof ApiError
          ? { tone: "bad", text: `The check could not be run (${e.status === 404 ? "this page's deployment record was not found" : `HTTP ${e.status}`}). Nothing was confirmed.` }
          : { tone: "warn", text: "Couldn't reach the server, so it is not known whether the check ran. The report below was reloaded from what the server recorded." };
    }
    rechecks.set(key, { busy: false, result });
    paintSources();
    await load(); // the report is the truth: states change only where the recorded anchors say so
  }

  function build() {
    if (built) return;
    built = true;
    body.replaceChildren(holders.counts, holders.notices, holders.links, holders.targets, holders.sources, holders.publications, holders.history);
  }

  async function load(): Promise<void> {
    const req = life.begin();
    if (!view) body.replaceChildren(loadingBlock("Loading created links"));
    else results.setAttribute("aria-busy", "true");
    try {
      const qs=new URLSearchParams(extraFilters);if(state!=='all')qs.set('state',state);
      const raw = await api<unknown>(`/v1/clients/${enc(clientId)}/links${qs.size?'?'+qs:''}`, { signal: req.signal });
      if (!req.current()) return;
      const parsed = parseClientLinks(raw);
      if (!parsed) throw new Error("The server's reply was not in the expected format.");
      view = parsed;
      evidenceFiltered=Object.keys(extraFilters).length>0;
      const providers=filterInputs.get('platform') as HTMLSelectElement;
      for(const platform of new Set(parsed.links.map(l=>l.source.platform).filter((p):p is string=>!!p)))if(![...providers.options].some(o=>o.value===platform))providers.append(h('option',{value:platform},platformLabel(platform)));
      build();
      paint();
      void loadDeployments();
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      const notFound = e instanceof ApiError && e.status === 404;
      const loadError = notFound ? "This client was not found. It may have been deleted." : e instanceof ApiError && e.status === 400 ? `The server refused the request: ${safeMessage(e.message)}` : safeMessage((e as Error).message || "The created links could not be loaded.");
      if (!view) {
        body.replaceChildren(errorBanner(loadError, notFound ? undefined : { retry: () => void load() }), ...(notFound ? [h("p", null, h("a", { href: "#/" }, "Back to the Fleet"))] : []));
      } else {
        results.replaceChildren(errorBanner(`Couldn't refresh: ${loadError} Showing what loaded earlier.`, { retry: () => void load() }));
      }
    } finally {
      results.removeAttribute("aria-busy");
    }
  }

  stateSelect.addEventListener("change", () => {
    state = parseStateFilter(stateSelect.value);
    void load();
  });
  queryInput.addEventListener("input", () => {
    query = queryInput.value;
    paintList();
  });
  await load();
}

// ---- all clients ----------------------------------------------------------------------------------------------------------

const PAGE_SIZE = 25;

export async function allLinksView(root: HTMLElement): Promise<void> {
  const shell = h("div", { class: "qf ql" });
  root.replaceChildren(shell);
  const life = createLifecycle(shell);
  let offset = 0;
  let includeAll = false;
  let loaded: ReturnType<typeof parseSummary> = null;

  const allBox = h("input", { id: "links-all", type: "checkbox", "aria-controls": "links-summary" }) as HTMLInputElement;
  const toolbar = h("div", { class: "qf-toolbar", role: "search", "aria-label": "Created links summary options" }, h("div", { class: "ql-check" }, allBox, h("label", { for: "links-all" }, "Include clients with no publications")));
  const note = h("p", { class: "qf-summary", role: "status", dataset: { summaryNote: "true" } });
  const results = h("div", { id: "links-summary" });
  const prev = h("button", { type: "button", id: "links-prev", class: "secondary", on: { click: () => go(-1) } }, "Previous") as HTMLButtonElement;
  const next = h("button", { type: "button", id: "links-next", class: "secondary", on: { click: () => go(1) } }, "Next") as HTMLButtonElement;
  const pager = h("div", { class: "btn-group ql-pager", role: "navigation", "aria-label": "Pages" }, prev, next);
  shell.append(h("div", { class: "topbar" }, h("h1", null, "Created links")), h("p", { class: "qf-summary" }, "Every client at a glance: the links CloudPush created, counted only when delivered or observed. Open a client for the exact source and target of each link. This is not a list of backlinks found elsewhere."), toolbar, note, results, pager, legend());

  const setDisabled = (b: HTMLButtonElement, off: boolean) => {
    if (off) b.setAttribute("aria-disabled", "true");
    else b.removeAttribute("aria-disabled");
  };
  function go(dir: -1 | 1) {
    if ((dir < 0 && prev.getAttribute("aria-disabled") === "true") || (dir > 0 && next.getAttribute("aria-disabled") === "true")) return;
    offset = Math.max(0, offset + dir * PAGE_SIZE);
    void load();
  }

  const num = (n: number) => h("td", null, String(n));
  function row(c: SummaryClientView): HTMLElement {
    return h(
      "tr",
      { dataset: { clientId: c.clientId } },
      h("td", { class: "qf-wrap" }, h("a", { class: "qf-business", href: clientLinksHref(c.clientId) }, c.businessName), h("div", { class: "muted" }, c.slug)),
      h("td", { dataset: { col: "active" } }, h("strong", null, String(c.counts.active))),
      num(c.counts.deliveredUninspected),
      num(c.counts.observed),
      h("td", { class: "ql-nc" }, String(c.counts.planned)),
      h("td", { class: "ql-nc" }, String(c.counts.uncertain)),
      h("td", { class: "ql-nc" }, String(c.counts.absent)),
      num(c.targets),
      num(c.sourcePages),
      num(c.publications),
      h(
        "td",
        { class: "qf-wrap" },
        c.warnings > 0 ? pill(`${c.warnings} unconfirmed`, "warn") : null,
        c.supersededReleaseReceipts > 0 ? h("div", { class: "muted" }, `${c.supersededReleaseReceipts} superseded-release ${c.supersededReleaseReceipts === 1 ? "receipt" : "receipts"} (history, not counted separately)`) : null,
        c.previousAccountReceipts > 0 ? h("div", { class: "muted" }, `${c.previousAccountReceipts} previous-account ${c.previousAccountReceipts === 1 ? "receipt" : "receipts"} (history, not counted)`) : c.warnings > 0 || c.supersededReleaseReceipts > 0 ? null : dash,
      ),
    );
  }

  function paint() {
    if (!loaded) return;
    const s = loaded;
    const pg = s.page;
    const from = s.clients.length === 0 ? 0 : (pg?.offset ?? offset) + 1;
    const to = (pg?.offset ?? offset) + s.clients.length;
    note.textContent = pg ? `Clients ${from}–${to} of ${pg.total}.${s.dropped ? ` ${s.dropped} unreadable ${s.dropped === 1 ? "row was" : "rows were"} skipped.` : ""}` : "";
    setDisabled(prev, (pg?.offset ?? offset) <= 0);
    setDisabled(next, !(pg?.hasMore ?? false));
    pager.hidden = !pg || (pg.total <= PAGE_SIZE && offset === 0);
    if (s.clients.length === 0) {
      results.replaceChildren(emptyBlock(includeAll ? "No clients" : "No client has published anything yet", includeAll ? "Add a client first." : "Clients appear here once a publication has been submitted for them. Tick the box above to list every client."));
      return;
    }
    const th = (label: string, cls?: string) => h("th", { scope: "col", class: cls }, label);
    const t = s.totalsThisPage;
    const mismatch = sumActive(s.clients) !== t.active;
    const table = h(
      "table",
      { class: "qf-table ql-table" },
      h("caption", { class: "qf-sr-only" }, "Created links per client. Counted columns first, then listed-but-not-counted columns."),
      h("thead", null, h("tr", null, th("Client"), th("Counted"), th("Delivered (not inspected)"), th("Observed"), th("Planned (not counted)", "ql-nc"), th("Uncertain (not counted)", "ql-nc"), th("Absent (not counted)", "ql-nc"), th("Target pages"), th("Source pages"), th("Publications"), th("Notes"))),
      h("tbody", null, ...s.clients.map(row)),
    );
    results.replaceChildren(
      ...(mismatch ? [notice("bad", "alert", h("strong", null, "The numbers disagree. "), `The clients listed add up to ${sumActive(s.clients)} counted links but the server's total for this page says ${t.active}.`)] : []),
      h(
        "div",
        { class: "ql-notcounted", dataset: { totalsThisPage: "true" } },
        h("strong", null, "Totals for this page only: "),
        h("span", { class: "ql-chip", dataset: { stat: "active" } }, `${t.active} counted`),
        h("span", { class: "ql-chip" }, `${t.deliveredUninspected} delivered (not inspected)`),
        h("span", { class: "ql-chip" }, `${t.observed} observed`),
        h("span", { class: "ql-chip" }, `${t.planned} planned (not counted)`),
        h("span", { class: "ql-chip" }, `${t.uncertain} uncertain (not counted)`),
        h("span", { class: "ql-chip" }, `${t.absent} absent (not counted)`),
      ),
      tableRegion("Created links per client", table),
    );
  }

  async function load(): Promise<void> {
    const req = life.begin();
    if (!loaded) results.replaceChildren(loadingBlock("Loading created links"));
    else results.setAttribute("aria-busy", "true");
    try {
      const raw = await api<unknown>(`/v1/links/summary?limit=${PAGE_SIZE}&offset=${offset}${includeAll ? "&all=1" : ""}`, { signal: req.signal });
      if (!req.current()) return;
      const parsed = parseSummary(raw);
      if (!parsed) throw new Error("The server's reply was not in the expected format.");
      loaded = parsed;
      paint();
    } catch (e) {
      if (isAbort(e) || !req.current()) return;
      const msg = safeMessage((e as Error).message || "The summary could not be loaded.");
      results.replaceChildren(errorBanner(loaded ? `Couldn't refresh: ${msg} Showing what loaded earlier.` : msg, { retry: () => void load() }));
    } finally {
      results.removeAttribute("aria-busy");
    }
  }

  allBox.addEventListener("change", () => {
    includeAll = allBox.checked;
    offset = 0;
    void load();
  });
  await load();
}
